import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PLAYBACK_RATES, formatPlaybackRate } from '@/presentation/util/playbackRate';
import { mediaControlLabelButtonClass } from '@/presentation/component/course/mediaPlayerStyles';

const optionClass = 'block w-full px-2 py-3 sm:py-2 text-sm leading-5 text-right tabular-nums';

interface PlaybackRateMenuProps {
  rate: number;
  onChange: (rate: number) => void;
}

export const PlaybackRateMenu = ({ rate, onChange }: PlaybackRateMenuProps) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setIsOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  const handleSelect = (selected: number) => {
    onChange(selected);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const optionStateClass = (option: number) =>
    option === rate
      ? 'bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)] font-semibold'
      : 'hover:bg-[var(--color-surface-2)]';

  const activeRateLabel = formatPlaybackRate(rate, i18n.language);

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        className={mediaControlLabelButtonClass}
        aria-label={t('mediaPlayer.playbackRateValue', { rate: activeRateLabel })}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {activeRateLabel}
      </button>

      {isOpen ? (
        <div
          role="radiogroup"
          aria-label={t('mediaPlayer.playbackRate')}
          className="absolute bottom-full right-0 mb-2 min-w-12 max-h-32 overflow-y-auto overscroll-contain sm:max-h-none sm:overflow-visible rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] py-1 text-[var(--color-ink)] shadow-lg"
        >
          {PLAYBACK_RATES.map((option) => (
            <button
              key={option}
              role="radio"
              aria-checked={option === rate}
              onClick={() => handleSelect(option)}
              className={`${optionClass} ${optionStateClass(option)}`}
            >
              {formatPlaybackRate(option, i18n.language)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
